if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.variantChangeUnsubscriber = undefined;
        this.currentMediaGroup = this.dataset.variantMediaGroup || '';
        this.variantMediaOptionPosition = Number(this.dataset.variantMediaOptionPosition || 0);
        this.initVariantMediaFilter();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      initVariantMediaFilter() {
        if (!this.currentMediaGroup || !this.variantMediaOptionPosition) return;

        this.filterMediaByGroup(this.currentMediaGroup, false);

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            const variant = data?.variant;
            if (!variant || String(data?.sectionId) !== String(this.id.replace('MediaGallery-', ''))) return;

            const optionValue = variant.options?.[this.variantMediaOptionPosition - 1];
            if (!optionValue) return;

            this.filterMediaByGroup(this.handleize(optionValue), true);
          });
        }
      }

      handleize(value) {
        return String(value)
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      filterMediaByGroup(group, resetActiveMedia = true) {
        this.currentMediaGroup = group;

        const shouldShow = (item) => {
          const itemGroup = item.dataset.mediaGroup || 'common';
          return itemGroup === 'common' || itemGroup === group;
        };

        this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((item) => {
          item.hidden = !shouldShow(item);
          item.setAttribute('aria-hidden', item.hidden ? 'true' : 'false');
        });

        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((item) => {
          item.hidden = !shouldShow(item);
          item.setAttribute('aria-hidden', item.hidden ? 'true' : 'false');
        });

        this.filterModalMedia(group);

        const visibleMedia = Array.from(
          this.elements.viewer?.querySelectorAll('[data-media-id]:not([hidden])') || []
        );
        const activeMedia = this.elements.viewer?.querySelector('[data-media-id].is-active');

        if (resetActiveMedia || activeMedia?.hidden) {
          const firstGroupMedia =
            visibleMedia.find((item) => item.dataset.mediaGroup === group) || visibleMedia[0];

          if (firstGroupMedia) {
            this.setActiveMedia(firstGroupMedia.dataset.mediaId, false);
          }
        }

        const total = visibleMedia.length;
        const totalCounter = this.querySelector('.slider-counter--total');

        if (totalCounter) {
          totalCounter.textContent = total;
        }

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      filterModalMedia(group) {
        const modal = document.querySelector(
          `#ProductModal-${this.id.replace('MediaGallery-', '')}`
        );

        if (!modal) return;

        modal.querySelectorAll('.product-media-modal__content > *').forEach((item) => {
          const image = item.matches('img') ? item : item.querySelector('img');

          if (!image) return;

          const alt = image.getAttribute('alt') || '';
          const match = alt.match(/\[group:(starter|explorer|creator)\]/i);
          const itemGroup = match ? match[1].toLowerCase() : 'common';

          item.hidden = itemGroup !== 'common' && itemGroup !== group;

          image.setAttribute(
            'alt',
            alt
              .replace(/\s*\[group:(starter|explorer|creator)\]\s*/gi, '')
              .trim()
          );
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );

            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );

        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}